import {
  PrimitiveValueType,
  RecordsSearchFilterResponse,
  RowDataReducerAction,
  RowDataObject,
  RowDataReducerState,
} from "../../../../../types";

const getPaySuffix = (propertyKey: string): "cp" | "ip" | "wp" | null => {
  if (propertyKey.endsWith("_cp")) return "cp";
  if (propertyKey.endsWith("_ip")) return "ip";
  if (propertyKey.endsWith("_wp")) return "wp";
  return null;
};

const normalizeTaxCode = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  return null;
};

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmedValue = value.trim();
  return trimmedValue === "" ? null : trimmedValue;
};

const getAppliesToScope = (appliesTo: unknown): "labor" | "parts" | "both" => {
  if (appliesTo === "Labor") return "labor";
  if (appliesTo === "Parts") return "parts";
  return "both";
};

const updateAccountRows = (
  rows: RowDataObject[] | undefined,
  keyPrefix: "labor" | "parts",
  paySuffix: "cp" | "ip" | "wp",
  storeValue: string | null,
  companyValue: string | null,
  accountValue: string | null,
): RowDataObject[] => {
  const storeKey = `${keyPrefix}_store_${paySuffix}`;
  const companyKey = `${keyPrefix}_company_${paySuffix}`;
  const accountKey = `${keyPrefix}_account_${paySuffix}`;
  const sourceRows = rows?.length ? rows : [{}];

  return sourceRows.map((row) => ({
    ...row,
    [storeKey]: storeValue,
    [companyKey]: companyValue,
    [accountKey]: accountValue,
  }));
};

export const updateDiscountTaxCodesByLaborType = (
  propertyKey: string,
  inputValue: PrimitiveValueType | undefined,
  response: RecordsSearchFilterResponse | undefined,
  selectedTabSectionKey: string | undefined,
  rowDataReducerState: RowDataReducerState,
  dispatch: React.Dispatch<RowDataReducerAction>,
): void => {
  const paySuffix = getPaySuffix(propertyKey);
  if (!paySuffix) return;

  const selectedLaborType = inputValue as string | null | undefined;
  const basePayload = selectedTabSectionKey ? { selectedTabSectionKey } : {};
  const sourceData = selectedTabSectionKey
    ? (rowDataReducerState.rowData[selectedTabSectionKey] as {
        [key: string]: unknown;
      })
    : (rowDataReducerState.rowData as {
        [key: string]: unknown;
      });
  const appliesToScope = getAppliesToScope(
    rowDataReducerState.rowData.applies_to ?? sourceData?.applies_to,
  );
  const laborTaxCodeKey = `labor_tax_code_${paySuffix}`;
  const partsTaxCodeKey = `parts_tax_code_${paySuffix}`;
  const laborAccountsKey = `labor_accounts_${paySuffix}`;
  const partsAccountsKey = `parts_accounts_${paySuffix}`;
  const existingLaborRows = sourceData?.[laborAccountsKey] as
    | RowDataObject[]
    | undefined;
  const existingPartsRows = sourceData?.[partsAccountsKey] as
    | RowDataObject[]
    | undefined;

  if (!selectedLaborType) {
    dispatch({
      type: "UPDATE_FIELD",
      payload: {
        propertyKey: laborTaxCodeKey,
        newFieldValue: null,
        ...basePayload,
      },
    });

    dispatch({
      type: "UPDATE_FIELD",
      payload: {
        propertyKey: partsTaxCodeKey,
        newFieldValue: null,
        ...basePayload,
      },
    });

    dispatch({
      type: "UPDATE_FIELD",
      payload: {
        propertyKey: laborAccountsKey,
        newFieldValue: updateAccountRows(
          existingLaborRows,
          "labor",
          paySuffix,
          null,
          null,
          null,
        ),
        ...basePayload,
      },
    });

    dispatch({
      type: "UPDATE_FIELD",
      payload: {
        propertyKey: partsAccountsKey,
        newFieldValue: updateAccountRows(
          existingPartsRows,
          "parts",
          paySuffix,
          null,
          null,
          null,
        ),
        ...basePayload,
      },
    });
    return;
  }

  const selectedLaborTypeData = response?.data?.find(
    (record) => record.labor_type === selectedLaborType,
  );

  const laborTaxCode = normalizeTaxCode(selectedLaborTypeData?.labor_tax_code);
  const partsTaxCode = normalizeTaxCode(selectedLaborTypeData?.parts_tax_code);
  const selectedStore = normalizeString(selectedLaborTypeData?.store_id);
  const selectedCompany = normalizeString(
    selectedLaborTypeData?.journal_source_co,
  );
  const laborSaleAccount = normalizeString(
    selectedLaborTypeData?.labor_sale_acct,
  );
  const partsSaleAccount = normalizeString(
    selectedLaborTypeData?.parts_sale_acct,
  );

  dispatch({
    type: "UPDATE_FIELD",
    payload: {
      propertyKey: laborTaxCodeKey,
      newFieldValue: laborTaxCode,
      ...basePayload,
    },
  });

  dispatch({
    type: "UPDATE_FIELD",
    payload: {
      propertyKey: partsTaxCodeKey,
      newFieldValue: partsTaxCode,
      ...basePayload,
    },
  });

  dispatch({
    type: "UPDATE_FIELD",
    payload: {
      propertyKey: laborAccountsKey,
      newFieldValue: updateAccountRows(
        existingLaborRows,
        "labor",
        paySuffix,
        appliesToScope === "parts" ? null : selectedStore,
        appliesToScope === "parts" ? null : selectedCompany,
        appliesToScope === "parts" ? null : laborSaleAccount,
      ),
      ...basePayload,
    },
  });

  dispatch({
    type: "UPDATE_FIELD",
    payload: {
      propertyKey: partsAccountsKey,
      newFieldValue: updateAccountRows(
        existingPartsRows,
        "parts",
        paySuffix,
        appliesToScope === "labor" ? null : selectedStore,
        appliesToScope === "labor" ? null : selectedCompany,
        appliesToScope === "labor" ? null : partsSaleAccount,
      ),
      ...basePayload,
    },
  });
};
